/**
 * BILLING-S1B — pricing page pública /planos.
 *
 * Read-only: consome /api/plans, renderiza 3 cards, toggle mensal/anual
 * persistido em localStorage, FAQ accordion, CTA mostra toast "Em breve".
 *
 * Sem auth, sem JWT. Acessível a qualquer visitante.
 *
 * Quando BILLING-S2 entrar, `handleSelect` vira chamada a
 * POST /api/billing/checkout-session + redirect Stripe — zero retrabalho
 * de UI.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import PlanCard from "@/components/billing/PlanCard";
import trace from "@/services/executionTrace";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const CYCLE_KEY = "nexo_pricing_cycle";

// ============================================================================
// FAQ data
// ============================================================================
const FAQ = [
  {
    id: "change_plan",
    q: "Posso mudar de plano depois?",
    a: "Sim, a qualquer momento. Upgrade é instantâneo. Downgrade mantém as features do plano atual até o fim do período pago.",
  },
  {
    id: "trial",
    q: "Como funciona o trial?",
    a: "Todo signup ganha 7 dias do Pro liberados, sem precisar de cartão. Depois do trial, sua conta segue no Essencial automaticamente — você decide se quer fazer upgrade.",
  },
  {
    id: "annual",
    q: "Anual vale a pena?",
    a: "Você paga 10 meses, usa 12 (equivalente a 20% off). Se mudar de plano antes de 12 meses, o ajuste é proporcional.",
  },
  {
    id: "limit",
    q: "E se eu atingir o limite?",
    a: "Você é avisado antes e pode fazer upgrade com um clique. No Premium, conversas acima de 500/dia entram em soft cap (R$ 0,30 cada), sem bloqueio.",
  },
];

// ============================================================================
// FAQ Item (accordion)
// ============================================================================
function FaqItem({ item, expanded, onToggle }) {
  return (
    <div className="border-b border-white/[0.04]" data-testid={`faq-item-${item.id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`faq-body-${item.id}`}
        className="w-full flex items-center justify-between py-4 text-left hover:text-cyan-400 transition-colors"
      >
        <span className="text-sm font-medium text-white">{item.q}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-500 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={`faq-body-${item.id}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-sm text-slate-400 leading-relaxed">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// PlansPage
// ============================================================================
export default function PlansPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState(() => {
    try {
      const raw = localStorage.getItem(CYCLE_KEY);
      return raw === "annual" ? "annual" : "monthly";
    } catch {
      return "monthly";
    }
  });
  const [expandedFaq, setExpandedFaq] = useState(null);

  // ── Fetch plans ─────────────────────────────────────────────────────────
  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/plans`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const list = data?.plans || [];
      setPlans(list);
    } catch (e) {
      setError(e?.message || "Falha ao carregar planos");
      toast.error("Não foi possível carregar os planos. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // ── Trace plans_viewed (1× por mount) ──────────────────────────────────
  useEffect(() => {
    if (!plans || plans.length === 0) return;
    try {
      trace.record("billing.plans_viewed", {
        cycle,
        n_plans: plans.length,
      });
    } catch {
      /* */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans?.length]);

  // ── Cycle toggle ────────────────────────────────────────────────────────
  const handleCycleChange = (next) => {
    if (next === cycle) return;
    const from = cycle;
    setCycle(next);
    try {
      localStorage.setItem(CYCLE_KEY, next);
    } catch {
      /* private mode */
    }
    try {
      trace.record("billing.cycle_toggled", { from, to: next });
    } catch {
      /* */
    }
  };

  // ── Plan select (S1B = só toast; S2 substitui por checkout) ─────────────
  const handleSelect = (slug, cyc) => {
    try {
      trace.record("billing.plan_intent", {
        plan_slug: slug,
        cycle: cyc,
      });
    } catch {
      /* */
    }
    toast.info(
      "Em breve — checkout será ativado após validação final dos planos.",
      { duration: 4000 },
    );
  };

  // ── FAQ ────────────────────────────────────────────────────────────────
  const handleFaqToggle = (id) => {
    const next = expandedFaq === id ? null : id;
    setExpandedFaq(next);
    if (next) {
      try {
        trace.record("billing.faq_expanded", { question_id: next });
      } catch {
        /* */
      }
    }
  };

  // ── Sorted plans (Pro primeiro no mobile via CSS order) ────────────────
  const sortedPlans = useMemo(() => {
    if (!Array.isArray(plans)) return [];
    return [...plans].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  }, [plans]);

  return (
    <div
      className="min-h-screen pb-24"
      style={{ background: "#0B0F1A" }}
      data-testid="plans-page"
    >
      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 pt-16 pb-12 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight mb-4"
        >
          Sua produtividade, amplificada por IA
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto"
        >
          Três planos pra cada momento. Comece grátis por 7 dias.
        </motion.p>
      </header>

      {/* Toggle Mensal / Anual */}
      <div className="max-w-md mx-auto px-6 mb-12 flex items-center justify-center gap-3">
        <div
          className="inline-flex items-center gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]"
          role="tablist"
          aria-label="Ciclo de cobrança"
          data-testid="cycle-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={cycle === "monthly"}
            onClick={() => handleCycleChange("monthly")}
            className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
              cycle === "monthly"
                ? "bg-cyan-500/15 text-cyan-300"
                : "text-slate-500 hover:text-slate-300"
            }`}
            data-testid="cycle-monthly"
          >
            Mensal
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={cycle === "annual"}
            onClick={() => handleCycleChange("annual")}
            className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
              cycle === "annual"
                ? "bg-cyan-500/15 text-cyan-300"
                : "text-slate-500 hover:text-slate-300"
            }`}
            data-testid="cycle-annual"
          >
            Anual
          </button>
        </div>
        {cycle === "annual" && (
          <motion.span
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ backgroundColor: "#f59e0b20", color: "#f59e0b" }}
          >
            Economize 20%
          </motion.span>
        )}
      </div>

      {/* Grid de planos */}
      <section className="max-w-6xl mx-auto px-6 mb-20">
        {loading ? (
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            data-testid="plans-loading"
          >
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="glass-card rounded-2xl p-6 h-[500px] animate-pulse"
                style={{ borderLeft: "3px solid #1F2A44" }}
              />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16" data-testid="plans-error">
            <p className="text-slate-400 text-sm mb-4">
              Não foi possível carregar os planos.
            </p>
            <button
              type="button"
              onClick={fetchPlans}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 transition-colors text-sm font-medium"
              data-testid="plans-retry"
            >
              <RotateCw className="w-4 h-4" />
              Tentar de novo
            </button>
          </div>
        ) : sortedPlans.length === 0 ? (
          <div className="text-center py-16 text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-3" />
            Planos sendo carregados...
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch"
            data-testid="plans-grid"
          >
            {sortedPlans.map((plan) => {
              const isPro = plan.slug === "pro";
              // Mobile order: Pro primeiro (order -1), Essencial (0), Premium (1).
              const mobileOrder = isPro ? -1 : plan.slug === "essencial" ? 0 : 1;
              return (
                <div
                  key={plan.slug}
                  style={{ order: mobileOrder }}
                  className="md:!order-none"
                >
                  <PlanCard
                    plan={plan}
                    cycle={cycle}
                    isFeatured={isPro}
                    onSelect={handleSelect}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 mb-20" data-testid="plans-faq">
        <h2 className="font-display text-2xl font-bold text-white mb-6 text-center">
          Perguntas frequentes
        </h2>
        <div className="glass-card rounded-2xl px-6">
          {FAQ.map((item) => (
            <FaqItem
              key={item.id}
              item={item}
              expanded={expandedFaq === item.id}
              onToggle={() => handleFaqToggle(item.id)}
            />
          ))}
        </div>
      </section>

      {/* R4 — Disclaimer legal antes da contratação, visível em todas as
          larguras. Linka diretamente para as páginas estáticas. */}
      <p
        className="max-w-3xl mx-auto px-6 mb-10 text-center text-xs text-slate-500"
        data-testid="plans-legal-disclaimer"
      >
        Ao assinar um plano, você concorda com os{" "}
        <Link
          to="/termos"
          className="text-cyan-400 hover:underline"
          data-testid="plans-link-termos"
        >
          Termos de Uso
        </Link>{" "}
        e a{" "}
        <Link
          to="/privacidade"
          className="text-cyan-400 hover:underline"
          data-testid="plans-link-privacidade"
        >
          Política de Privacidade
        </Link>
        .
      </p>

      {/* Footer */}
      <footer
        className="max-w-3xl mx-auto px-6 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500"
        data-testid="plans-footer"
      >
        {!user && (
          <Link to="/login" className="hover:text-cyan-400 transition-colors">
            Já tem conta? Entrar
          </Link>
        )}
        {user && (
          <Link to="/" className="hover:text-cyan-400 transition-colors">
            ← Voltar ao app
          </Link>
        )}
        <Link
          to="/privacidade"
          className="hover:text-cyan-400 transition-colors"
          data-testid="plans-footer-privacidade"
        >
          Política de Privacidade
        </Link>
        <Link
          to="/termos"
          className="hover:text-cyan-400 transition-colors"
          data-testid="plans-footer-termos"
        >
          Termos de Uso
        </Link>
      </footer>
    </div>
  );
}
